import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Topbar from '../components/Topbar';
import DarkCard from '../components/DarkCard';
import DarkButton from '../components/DarkButton';
import ThemeRoot from '../components/ThemeRoot';
import { BadgeIcon } from '../svg/icons';
import { haptic } from '../lib/haptic';
import { useDarkStore } from '../store/darkStore';
import { callOpenAI } from '../../lib/functions';
import { STAGE_CONFIGS, STAGE_ORDER } from '../../data/stageConfigs';
import { parsePlanSections, downloadPlanPdf } from '../lib/planPdf';
import PublishModal from '../../components/shared/PublishModal';
import { useAuthStore } from '../../stores/authStore';

const SYSTEM_PROMPT = `You are KoKo, a warm, cheeky co-founder fox mentor for a young founder
aged 8-14. They have just finished all six stages of their business plan.

Write a FULL, detailed business plan document IN THEIR VOICE. Use exactly
these eleven sections, in this order, each starting with "## " on its own line.
Every section must be substantial — 4 to 6 sentences minimum, written in
flowing paragraphs (no bullet lists).

## The Big Idea
Describe what the business is, the spark behind it, and why it genuinely
matters. Bring in the founder's exact words from Stage 1. Paint a picture of
what the world looks like once this business exists. Explain the core problem
it solves and why now is the right moment for it.

## The Customer
Describe the specific people this business serves — their age, situation, and
why they need this. Use detail from Stage 2 to paint a vivid picture of one
real person who would love this. Explain what their life looks like before and
after discovering this business. Describe how you would find and reach them.

## The Problem Worth Solving
Dig into the pain or gap in the market. Pull from the problem diary and any
Stage 2 insights. Explain why existing solutions fall short. Show that the
founder really understands the frustration from the customer's point of view.

## The Solution
Walk through exactly how the business delivers its product or service. Draw
on Stage 3 answers about building, sourcing, or delivering. Explain what makes
the experience special or different. Describe the first version (MVP) and how
it could grow.

## The Competition & Edge
Name who else is out there solving a similar problem. Pull from Stage 6
competitive answers. Explain what makes this business different — its unique
angle, voice, or approach. Describe why customers would choose this over
alternatives.

## Marketing & Story
Describe the marketing plan — how the world will hear about this business.
Pull the hook and story from Stage 4. Explain where the founder will show up
(social, word of mouth, events, etc.). Describe the message that will make
people stop and pay attention.

## The Numbers
Walk through the financial picture clearly. Pull pricing and cost data from
Stage 5. Explain the cost to make or deliver, what customers pay, and the
profit per sale. Describe how revenue could grow over time if things go well.

## The Elevator Pitch
Write a polished, confident 3–4 sentence pitch the founder could say out loud
to anyone. Draw directly from their Stage 6 pitch answers. It should be clear,
exciting, and end with why someone should care right now. Make it sound
genuinely like the founder's voice.

## Founder Superpowers
Name three specific strengths this founder has shown across all six stages —
grounded in what they actually wrote. Be specific, not generic. Explain why
each strength matters for building this particular business.

## 90-Day Action Plan
Give four concrete, numbered steps the founder can take in the next 90 days.
Each step should be specific, doable for a young person, and build on what
they have already answered. Think: build, test, talk to customers, improve.

## Final Cheer
End with a warm, energising paragraph — 3–4 sentences — that celebrates how
far they have come, names something unique about their vision, and sends them
off with genuine excitement. Make it personal, not generic.

Rules:
- British English throughout. Speak directly to them ("you", "your").
- Ground EVERY section in their ACTUAL answers — no made-up facts, no
  generic filler. If a section is sparse, acknowledge it gently and offer
  a thoughtful interpretation of what little they shared.
- Warm, clear, age-appropriate prose. No bullet lists anywhere.
- Each section must feel substantial — a proud mentor writing a real document.
- Maximum two emoji across the entire plan.`;

function buildBrief(
  answers: Record<string, Record<string, string>>,
  explorerName: string,
): string {
  const lines: string[] = [];
  lines.push(`Founder: ${explorerName || 'Explorer'}`);
  for (const key of STAGE_ORDER) {
    const cfg = STAGE_CONFIGS[key];
    if (!cfg) continue;
    const stageAnswers = answers[key] ?? {};
    const filled = cfg.questions
      .map((q) => {
        const v = (stageAnswers[q.id] ?? '').toString().trim();
        return v ? `  - ${q.prompt} -> ${v}` : '';
      })
      .filter(Boolean);
    if (filled.length === 0) continue;
    lines.push(`\nStage ${cfg.number} - ${cfg.name}:`);
    lines.push(...filled);
  }
  return lines.join('\n').slice(0, 4500);
}

const FALLBACK_PLAN = `## Your Idea
Your idea is the spark - a business rooted in what you care about, built
for people you understand. Hold on to why it matters to you; that is the
engine the rest of the plan runs on.

## Who It's For
You've pictured a real person who needs this. That clarity beats cleverness
every time. Keep them in mind for every future choice.

## Making It Real
You have a plan to actually build and deliver it. Start small, test fast,
learn from the first hands that hold it.

## Telling The World
Your story has a hook. A good pitch makes people want to repeat it to a
friend - aim for that.

## The Money Bit
Numbers are simple promises to keep. Know what something costs, what
people will pay, and why the gap makes sense.

## The Pitch
When you say it out loud, make it feel inevitable. Short, true, and with
a shape someone can remember an hour later.

## Your Superpowers
You turn up with specifics, not slogans. You'd rather answer than pose.

## Sharpen Next
Pick the one idea that made you grin the most and spend a week sharpening
just that.

## Final Cheer
Keep going, founder - the world loves a builder who ships.`;

export default function Summary() {
  const navigate = useNavigate();
  const { stages, answers, explorerName, summaryCache, setSummaryCache } = useDarkStore();
  const user = useAuthStore((s) => s.user);

  const [planText, setPlanText] = useState<string | null>(null);
  const [planLoading, setPlanLoading] = useState(true);
  const [planError, setPlanError] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showPublish, setShowPublish] = useState(false);
  const calledRef = useRef(false);

  const allDone = stages.every((s) => s.status === 'done');

  const businessName = useMemo(() => {
    const s1 = (answers as Record<string, Record<string, string>>).ideaExplosion ?? {};
    const raw = (s1.s1_q1 ?? '').toString().trim();
    if (raw) return raw;
    return explorerName ? `${explorerName}'s first plan` : 'My First Plan';
  }, [answers, explorerName]);

  const handlePublishClick = () => {
    haptic('tap');
    if (!user) {
      navigate('/auth?redirect=/home/summary');
      return;
    }
    setShowPublish(true);
  };

  useEffect(() => {
    haptic('levelUp');
  }, []);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;
    const allAnswers = answers as Record<string, Record<string, string>>;
    const inputHash = JSON.stringify(allAnswers);
    if (summaryCache && summaryCache.inputHash === inputHash) {
      setPlanText(summaryCache.text);
      setPlanLoading(false);
      return;
    }
    const brief = buildBrief(allAnswers, explorerName);
    callOpenAI({ systemPrompt: SYSTEM_PROMPT, prompt: brief })
      .then((res) => {
        const text = res.data?.responseText?.trim();
        if (text) {
          setPlanText(text);
          setSummaryCache(inputHash, text);
        } else {
          setPlanText(FALLBACK_PLAN);
          setPlanError(true);
        }
      })
      .catch((err) => {
        console.warn('KoKo plan call failed, using fallback:', err);
        setPlanText(FALLBACK_PLAN);
        setPlanError(true);
      })
      .finally(() => setPlanLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sections = useMemo(
    () => (planText ? parsePlanSections(planText) : []),
    [planText],
  );

  const stageBlocks = useMemo(
    () =>
      STAGE_ORDER.map((key) => {
        const cfg = STAGE_CONFIGS[key];
        const stageAnswers = (answers as Record<string, Record<string, string>>)[key] ?? {};
        const filled = cfg.questions
          .map((q) => ({
            prompt: q.prompt,
            value: (stageAnswers[q.id] ?? '').toString().trim(),
          }))
          .filter((a) => a.value.length > 0);
        return { cfg, filled };
      }),
    [answers],
  );

  const handleDownload = async () => {
    if (!planText || downloading) return;
    haptic('levelUp');
    setDownloading(true);
    try {
      downloadPlanPdf({
        planText,
        answers: answers as Record<string, Record<string, string>>,
        explorerName,
      });
    } catch (err) {
      console.error('PDF download failed:', err);
    } finally {
      // Small delay so the button state change is visible.
      setTimeout(() => setDownloading(false), 600);
    }
  };

  return (
    <ThemeRoot>
      <Topbar />

      <main className="mx-auto max-w-3xl px-4 py-8 md:py-12">
        <div className="flex flex-col items-center text-center">
          <BadgeIcon size={120} label="★" />
          <h1 className="mt-4 font-marker text-4xl text-amber md:text-5xl">
            Your Business Plan
          </h1>
          <p className="mt-2 max-w-md font-gill text-gray-600 dark:text-ink/80">
            {allDone
              ? `Boom, ${explorerName || 'founder'} — all six stages cleared.`
              : 'A sneak peek — finish all six stages to unlock the full reveal.'}
          </p>

          {allDone && (
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              <DarkButton
                accent="mint"
                hapticKind="levelUp"
                disabled={planLoading || downloading || !planText}
                onClick={handleDownload}
              >
                {downloading
                  ? 'Preparing PDF…'
                  : planLoading
                    ? 'KoKo is writing…'
                    : '⬇ Download as PDF'}
              </DarkButton>
              <DarkButton
                accent="violet"
                hapticKind="levelUp"
                disabled={planLoading || !planText}
                onClick={handlePublishClick}
              >
                ✨ Publish to Spotlight
              </DarkButton>
            </div>
          )}
        </div>

        {/* KoKo's full write-up */}
        <DarkCard active className="mt-8">
          <div className="mb-3 flex items-center gap-2">
            <span className="text-xl">🦊</span>
            <span className="font-marker text-sm uppercase tracking-[0.2em] text-amber">
              KoKo's Write-Up
            </span>
          </div>

          {planLoading && (
            <div className="flex items-center gap-1 py-2">
              {[0, 150, 300].map((delay) => (
                <span
                  key={delay}
                  className="koko-dot inline-block h-2 w-2 rounded-full bg-gray-400 dark:bg-ink/70"
                  style={{ animationDelay: `${delay}ms` }}
                />
              ))}
              <span className="ml-3 font-gill text-sm text-ink/60">
                KoKo is reading your whole plan…
              </span>
            </div>
          )}

          {!planLoading && sections.length > 0 && (
            <div className="space-y-5">
              {sections.map((sec, i) => (
                <div
                  key={`${sec.title}-${i}`}
                  className="rounded-2xl border border-white/5 bg-base/60 p-4"
                >
                  <div className="font-marker text-[11px] uppercase tracking-[0.22em] text-mint">
                    {sec.title}
                  </div>
                  <p className="mt-2 whitespace-pre-wrap font-gill text-base leading-relaxed text-ink">
                    {sec.body}
                  </p>
                </div>
              ))}
            </div>
          )}

          {!planLoading && sections.length === 0 && planText && (
            <p className="whitespace-pre-wrap font-gill text-base leading-relaxed text-ink">
              {planText}
            </p>
          )}

          {!planLoading && planError && (
            <div className="mt-4 font-gill text-xs italic text-gray-500 dark:text-ink/50">
              (Showing a warm-up summary — live KoKo needs a signed-in session.)
            </div>
          )}
        </DarkCard>

        {/* Stage-by-stage recap */}
        <div className="mt-6 grid gap-3">
          {stageBlocks.map(({ cfg, filled }) => (
            <DarkCard key={cfg.key}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{cfg.emoji}</span>
                  <div>
                    <div className="font-marker text-[11px] uppercase tracking-[0.2em] text-amber">
                      Stage {cfg.number}
                    </div>
                    <div className="font-marker text-lg text-gray-900 dark:text-ink">
                      {cfg.name}
                    </div>
                  </div>
                </div>
                <span className="rounded-full border border-mint/40 bg-mint/10 px-3 py-1 font-marker text-xs text-mint-dim dark:text-mint">
                  {filled.length} / {cfg.questions.length}
                </span>
              </div>
              {filled.length > 0 ? (
                <ul className="mt-3 space-y-2">
                  {filled.map((f, i) => (
                    <li
                      key={i}
                      className="rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-white/5 dark:bg-base/60"
                    >
                      <div className="font-marker text-[11px] uppercase tracking-wider text-gray-500 dark:text-ink/60">
                        {f.prompt}
                      </div>
                      <div className="mt-1 font-gill text-sm text-gray-900 dark:text-ink">
                        {f.value}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="mt-3 font-gill text-sm italic text-gray-500 dark:text-ink/50">
                  No answers yet — finish this stage to fill it in.
                </div>
              )}
            </DarkCard>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <DarkButton accent="ghost" onClick={() => navigate('/home/plans')}>
            My Plans
          </DarkButton>
          <DarkButton accent="ghost" onClick={() => navigate('/home')}>
            Back to Launchpad
          </DarkButton>
          <DarkButton
            accent="amber"
            hapticKind="levelUp"
            disabled={planLoading || downloading || !planText}
            onClick={handleDownload}
          >
            {downloading ? 'Preparing PDF…' : '⬇ Download as PDF'}
          </DarkButton>
          <DarkButton
            accent="mint"
            hapticKind="levelUp"
            className="shadow-glow-mint"
            onClick={() => navigate('/home/progress')}
          >
            See XP breakdown
          </DarkButton>
        </div>
      </main>

      <PublishModal
        visible={showPublish}
        onClose={() => setShowPublish(false)}
        defaultContentType="business"
        businessName={businessName}
        explorerName={explorerName}
        planText={planText || ''}
        planAnswers={answers as Record<string, Record<string, string>>}
        defaultCaption={
          explorerName
            ? `I just finished my business plan for ${businessName}! 🚀`
            : `Check out my business plan: ${businessName}!`
        }
      />
    </ThemeRoot>
  );
}
